import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  Input,
  DatePicker,
  Button,
  Spin,
  Divider,
  notification,
  Tag,
  Switch,
} from "antd";
import { Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { useSelector } from "react-redux";
import useStockMovementContext from "../../../../hooks/useStockMovementContext";
import ButtonStandard from "../../../../components/utils/button";
import {
  cnpjMask,
  numberMask,
} from "../../../../services/helpersMethodsService";
import {
  buildLinesFromNfItems,
  emptyMaterialLine,
  requiresSupplierFields,
  hasMaterialLineErrors,
} from "../../../../utils/stockMovement";
import {
  ENTRY_ORIGINS,
  EXIT_ORIGINS,
  DOCUMENT_TYPES,
  toI18nKey,
} from "./stockMovementConstants";
import StockMovementMaterialLine from "./StockMovementMaterialLine";
import DanfeViewerModal from "../../../../components/utils/invoiceUploadViewer/DanfeViewerModal";
import { StyledFormContainer, FiscalBlock, TotalCostBox } from "./styles";
import InvoiceUploadViewer from "../../../../components/utils/invoiceUploadViewer";
import SearchSupplier from "../../../../components/utils/searchSupplier";
import FinancialTransactionBox from "./FinancialTransactionBox";
import useWindowSize from "../../../../hooks/useWindowSize";
import { getParameterItems } from "../../../../services/generalParameterService";
import { searchSupplierByName } from "../../../../services/supplierService";

const { Option } = Select;
const { TextArea } = Input;

const INITIAL_HEADER = {
  origin: undefined,
  documentType: "INVOICE",
  documentFile: null,
  documentParsed: null,
  productionDate: "",
  issueDate: "",
  nfReference: "",
  nfDate: "",
  nfIssuerCompanyName: "",
  nfIssuerCnpj: "",
  receiptDate: "",
  supplierId: undefined,
  issuerCnpj: "",
  requester: "",
  observations: "",
  showFinancialTransactionBox: true,
  financialTransaction: {
    supplierId: null,
    documentNumber: null,
    billingDate: null,
    expirationDate: null,
    mainValue: null,
    mainValueCurrency: "BRL",
    fees: null,
    discount: null,
    value: null,
    payment: "inCash",
    installments: [],
    installmentTotal: null,
    action: "launch",
    settleDate: null,
    financialNatureId: null,
    financialClassId: null,
    resultCenterApportionmentDefault: true,
    customApportionmentValues: null,
    financialProjectId: null,
    financialIssueId: null,
    financialPaymentMethodId: null,
    financialTaxableDeductibleId: null,
    bankAccountFarmId: null,
    description: null,
  },
};

const CODES_PARAMETERS = [4003, 4004, 4005, 4006];

const StockMovementFormModal = () => {
  const size = useWindowSize();

  const { translation } = useSelector((state) => state.app);
  const tm = translation?.stockItem?.movements || {};
  const tf = tm.form || {};
  const defaultPlaceholder =
    translation?.defaultPlaceholder || "Informe um valor";

  const {
    isModalVisible,
    isLoadingEdit,
    newMovementType,
    groupId,
    farmId,
    handleSaveMovement,
    handleCloseModal,
    isLoadingRequest,
    textSearchSupplier,
    setTextSearchSupplier,
  } = useStockMovementContext();

  const [parametersValue, setParametersValue] = useState([]);
  const [lines, setLines] = useState([emptyMaterialLine()]);
  const [danfeVisible, setDanfeVisible] = useState(false);

  const type = newMovementType || "ENTRY";
  const origins = type === "ENTRY" ? ENTRY_ORIGINS : EXIT_ORIGINS;

  const validationSchema = Yup.object().shape({
    origin: Yup.string()
      .nullable()
      .required(tm.error?.originRequired || "Selecione a origem do movimento."),
    nfReference: Yup.string().when("origin", {
      is: (origin) =>
        (type === "ENTRY" && origin === "PURCHASE") ||
        (type === "EXIT" && origin === "SALE"),
      then: Yup.string().required(
        tm.error?.nfReferenceRequired || "Informe o número do documento.",
      ),
      otherwise: Yup.string().notRequired().nullable(),
    }),
    nfDate: Yup.date().when("origin", {
      is: (origin) =>
        (type === "ENTRY" && origin === "PURCHASE") ||
        (type === "EXIT" && origin === "SALE"),
      then: Yup.date().required(
        tm.error?.nfDateRequired || "Informe a data de recebimento.",
      ),
      otherwise: Yup.date().notRequired().nullable(),
    }),
    productionDate: Yup.date().when("origin", {
      is: (origin) => type === "ENTRY" && origin === "PRODUCTION",
      then: Yup.date().required(
        tm.error?.productionDateRequired || "Informe a data de produção.",
      ),
      otherwise: Yup.date().notRequired().nullable(),
    }),
    issueDate: Yup.date().when("origin", {
      is: (origin) => type === "EXIT" && origin === "WITHDRAWAL",
      then: Yup.date().required(
        tm.error?.issueDateRequired || "Informe a data de retirada.",
      ),
      otherwise: Yup.date().notRequired().nullable(),
    }),
    requester: Yup.string().when("origin", {
      is: (origin) => type === "EXIT" && origin === "WITHDRAWAL",
      then: Yup.string().required(
        tm.error?.requesterRequired || "Informe o solicitante.",
      ),
    }),
    supplierId: Yup.mixed().when("origin", {
      is: (origin) => requiresSupplierFields(type, origin),
      then: Yup.mixed().required(
        tm.error?.supplierRequired || "Selecione o fornecedor.",
      ),
    }),
    financialTransaction: Yup.object().when(
      ["origin", "showFinancialTransactionBox"],
      {
        is: (origin, showFinancialTransactionBox) =>
          showFinancialTransactionBox &&
          ((type === "ENTRY" && origin === "PURCHASE") ||
            (type === "EXIT" && origin === "SALE")),
        then: Yup.object().shape({
          documentNumber: Yup.string().nullable().required(),
          supplierId: Yup.string().nullable().required(),
          billingDate: Yup.date().nullable().required(),
          expirationDate: Yup.date().nullable().required(),
          financialClassId: Yup.string().nullable().required(),
          financialNatureId: Yup.string().nullable().required(),
          resultCenterApportionmentDefault: Yup.boolean().required(),
          mainValue: Yup.number().min(0).max(100000000).required(),
          value: Yup.number().min(0).max(100000000).required(),
          payment: Yup.string().required(),
          installments: Yup.array()
            .notRequired()
            .when("payment", {
              is: (payment) => payment === "part",
              then: Yup.array().required().min(1),
              otherwise: Yup.array().notRequired(),
            }),
          settleDate: Yup.date()
            .notRequired()
            .nullable()
            .when("action", {
              is: (action) => action === "launchAndSettle",
              then: Yup.date().required(),
              otherwise: Yup.date().notRequired().nullable(),
            }),
        }),
        otherwise: Yup.object().notRequired().nullable(),
      },
    ),
  });

  const isEnablePaymentMethod = useMemo(
    () => parametersValue.find((p) => p.code === 4004)?.value === "true",
    [parametersValue],
  );
  const isEnableFinancialIssue = useMemo(
    () => parametersValue.find((p) => p.code === 4003)?.value === "true",
    [parametersValue],
  );
  const isEnableFinancialTaxableDeductible = useMemo(
    () => parametersValue.find((p) => p.code === 4005)?.value === "true",
    [parametersValue],
  );
  const isEnableBankAccountFarm = useMemo(
    () => parametersValue.find((p) => p.code === 4006)?.value === "true",
    [parametersValue],
  );

  const resetLocalState = () => {
    setLines([emptyMaterialLine()]);
    setDanfeVisible(false);
  };

  const handleLineChange = (index, newLine) =>
    setLines((prev) => prev.map((l, i) => (i === index ? newLine : l)));
  const handleAddLine = () =>
    setLines((prev) => [...prev, emptyMaterialLine()]);
  const handleRemoveLine = (index) =>
    setLines((prev) => prev.filter((_, i) => i !== index));

  const totalItemsCost = lines.reduce(
    (total, line) => total + (line.quantity || 0) * (line.cost || 0),
    0,
  );

  const label = (group, value) =>
    (tf[group] && tf[group][toI18nKey(value)]) ||
    (tm[group] && tm[group][toI18nKey(value)]) ||
    value;

  const handleUpload = async (file, parsedData, setFieldValue) => {
    setFieldValue("documentFile", file);

    setFieldValue("documentParsed", parsedData);
    setFieldValue("nfReference", parsedData?.numero_nf || "");
    setFieldValue(
      "financialTransaction.documentNumber",
      parsedData?.numero_nf || "",
    );
    setFieldValue("nfDate", parsedData?.data_emissao || "");
    setFieldValue("nfIssuerCompanyName", parsedData?.emit_razao_social || "");
    setFieldValue("nfIssuerCnpj", parsedData?.emit_cnpj || "");
    setFieldValue("issuerCnpj", parsedData?.emit_cnpj || "");

    setFieldValue(
      "financialTransaction.billingDate",
      parsedData?.data_emissao || "",
    );

    if (parsedData?.emit_razao_social) {
      try {
        const {
          data: {
            results: { content: suppliers },
          },
        } = await searchSupplierByName({
          groupId,
          farmId,
          name: parsedData?.emit_razao_social,
        });
        if (suppliers && suppliers.length === 1) {
          setFieldValue("supplierId", suppliers[0].id);
          setTextSearchSupplier(suppliers[0].name);
        }
      } catch (error) {}
    }

    const newLines = await buildLinesFromNfItems(
      groupId,
      farmId,
      parsedData?.itens || [],
    );
    if (newLines) {
      setLines(newLines);
      // Quando o parser real existir, popular também o cabeçalho da NF aqui.
      setFieldValue("documentType", "INVOICE");
    }
  };

  useEffect(
    () => {
      async function getParameters() {
        try {
          const dataResult = [];
          for (let i = 0; i < CODES_PARAMETERS.length; i++) {
            const code = CODES_PARAMETERS[i];
            const {
              data: { results },
            } = await getParameterItems({ groupId, farmId, code });
            dataResult.push(results);
          }
          setParametersValue(dataResult);
        } catch (error) {}
      }
      if (isModalVisible) {
        getParameters();
      }
    },
    [farmId, groupId, isModalVisible],
    groupId,
    [isModalVisible],
  );

  return (
    <Formik
      enableReinitialize
      initialValues={INITIAL_HEADER}
      validationSchema={validationSchema}
      onSubmit={(values, { setFieldError, resetForm }) => {
        const showFinancialTransactionBox =
          (values.showFinancialTransactionBox &&
            type === "ENTRY" &&
            values.origin === "PURCHASE") ||
          (values.showFinancialTransactionBox &&
            type === "EXIT" &&
            values.origin === "SALE");
        const hasLineErrors =
          lines.length === 0 ||
          lines.some((l) => hasMaterialLineErrors(l, type));
        if (hasLineErrors) {
          notification.warning({
            message:
              tm.error?.lineRequired ||
              "Adicione ao menos um item com quantidade.",
          });
          return;
        }
        if (
          showFinancialTransactionBox &&
          isEnableBankAccountFarm &&
          (values.financialTransaction.bankAccountFarmId === undefined ||
            values.financialTransaction.bankAccountFarmId === null)
        ) {
          setFieldError(
            "financialTransaction.bankAccountFarmId",
            "Campo obrigatório",
          );
          return;
        }
        const isFinancialTransactionApplicable =
          showFinancialTransactionBox &&
          type === "ENTRY" &&
          values.origin === "PURCHASE";
        const payloadValues = {
          ...values,
          financialTransaction: isFinancialTransactionApplicable
            ? { ...values.financialTransaction, transactionType: "expense" }
            : null,
        };
        handleSaveMovement(
          lines,
          { ...payloadValues, type },
          resetForm,
          resetLocalState,
        );
      }}
    >
      {({
        values,
        errors,
        submitCount,
        setFieldValue,
        handleSubmit,
        resetForm,
      }) => {
        const hasError = (field) => submitCount > 0 && errors[field];

        const showFiscalFields =
          (type === "ENTRY" && values.origin === "PURCHASE") ||
          (type === "EXIT" &&
            (values.origin === "SALE" || values.origin === "TRANSFER"));
        const showProductionFields =
          type === "ENTRY" && values.origin === "PRODUCTION";
        const showWithDrawalFields =
          type === "EXIT" && values.origin === "WITHDRAWAL";
        const needsSupplierFields = requiresSupplierFields(type, values.origin);
        const showFinancialTransactionBox =
          (values.showFinancialTransactionBox &&
            type === "ENTRY" &&
            values.origin === "PURCHASE") ||
          (values.showFinancialTransactionBox &&
            type === "EXIT" &&
            values.origin === "SALE");
        // const showAuditWarning =
        //   type === "EXIT" &&
        //   (values.origin === "LOSS" || values.origin === "ADJUSTMENT");

        const handleCancel = () => {
          resetForm();
          resetLocalState();
          handleCloseModal();
        };

        return (
          <Modal
            title={
              type === "ENTRY"
                ? tf.title?.entry || "Entrada de Material"
                : tf.title?.exit || "Saída de Material"
            }
            visible={isModalVisible}
            width={size.width < 768 ? "100%" : size.width * 0.8}
            maskClosable={false}
            footer={null}
            onCancel={handleCancel}
            destroyOnClose
          >
            <Spin spinning={isLoadingEdit || isLoadingRequest}>
              <StyledFormContainer>
                <Form layout="vertical">
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        required
                        label={
                          <span
                            className={hasError("origin") ? "label-error" : ""}
                          >
                            {tf.origin || "Origem"} *
                          </span>
                        }
                        validateStatus={hasError("origin") ? "error" : ""}
                        help={hasError("origin") ? errors.origin : null}
                      >
                        <Select
                          value={values.origin}
                          onChange={(val) => {
                            setFieldValue("origin", val);
                            if (val !== "PURCHASE" && val !== "SALE") {
                              setFieldValue(
                                "showFinancialTransactionBox",
                                false,
                              );
                            }
                          }}
                          placeholder="Selecione"
                          allowClear
                        >
                          {origins.map((o) => (
                            <Option key={o} value={o}>
                              {type === "ENTRY"
                                ? label("entryOrigins", o)
                                : label("exitOrigins", o)}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  {showFiscalFields && (
                    <FiscalBlock>
                      <Row gutter={16} type="flex" align="middle">
                        <Col span={8}>
                          <Form.Item
                            label={tf.documentType || "Tipo Documento"}
                          >
                            <Select
                              value={values.documentType}
                              onChange={(val) =>
                                setFieldValue("documentType", val)
                              }
                            >
                              {DOCUMENT_TYPES.map((d) => (
                                <Option key={d} value={d}>
                                  {label("documentTypes", d)}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={16}>
                          <InvoiceUploadViewer
                            value={{
                              file: values.documentFile,
                              invoice: values.documentParsed,
                            }}
                            onChange={(file, parsedData) =>
                              handleUpload(file, parsedData, setFieldValue)
                            }
                          />
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col span={6}>
                          <Form.Item
                            label={
                              <span
                                className={
                                  hasError("nfReference") ? "label-error" : ""
                                }
                              >
                                {`${tf.nfReference}*` || "Nº Documento*"}
                              </span>
                            }
                            validateStatus={
                              hasError("nfReference") ? "error" : ""
                            }
                            help={
                              hasError("nfReference")
                                ? errors.nfReference
                                : null
                            }
                          >
                            <Input
                              value={values.nfReference}
                              disabled={!!values.documentFile}
                              placeholder={defaultPlaceholder}
                              onChange={(e) => {
                                setFieldValue("nfReference", e.target.value);
                                setFieldValue(
                                  "financialTransaction.documentNumber",
                                  e.target.value,
                                );
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            label={
                              <labe
                                className={
                                  hasError("nfDate") ? "label-error" : ""
                                }
                              >
                                {tf.nfDate || "Data Emissão"}*
                              </labe>
                            }
                            validateStatus={hasError("nfDate") ? "error" : ""}
                            help={hasError("nfDate") ? errors.nfDate : null}
                          >
                            <DatePicker
                              style={{ width: "100%" }}
                              format="DD/MM/YYYY"
                              disabled={!!values.documentFile}
                              value={
                                values.nfDate ? moment(values.nfDate) : null
                              }
                              onChange={(date) =>
                                setFieldValue(
                                  "nfDate",
                                  date ? date.format("YYYY-MM-DD") : "",
                                )
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            label={
                              <span>
                                {tf.receiptDate || "Data Recebimento"}
                              </span>
                            }
                          >
                            <DatePicker
                              style={{ width: "100%" }}
                              format="DD/MM/YYYY"
                              value={
                                values.receiptDate
                                  ? moment(values.receiptDate)
                                  : null
                              }
                              onChange={(date) => {
                                setFieldValue(
                                  "receiptDate",
                                  date ? date.format("YYYY-MM-DD") : "",
                                );
                                if (showFinancialTransactionBox) {
                                  setFieldValue(
                                    "financialTransaction.billingDate",
                                    date ? date.format("YYYY-MM-DD") : "",
                                  );
                                }
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            required={needsSupplierFields}
                            label={
                              <span
                                className={
                                  hasError("supplierId") ? "label-error" : ""
                                }
                              >
                                {tf.supplier || "Fornecedor"}
                                {needsSupplierFields && " *"}
                              </span>
                            }
                            validateStatus={
                              hasError("supplierId") ? "error" : ""
                            }
                            help={
                              hasError("supplierId") ? errors.supplierId : null
                            }
                          >
                            <SearchSupplier
                              textSearch={textSearchSupplier}
                              setTextSearch={setTextSearchSupplier}
                              value={values.supplierId}
                              handleChange={(val, supplier) => {
                                setFieldValue("supplierId", val);
                                setFieldValue(
                                  "financialTransaction.supplierId",
                                  val,
                                );
                                if (values.nfIssuerCnpj == null) {
                                  setFieldValue(
                                    "issuerCnpj",
                                    supplier?.document,
                                  );
                                }
                              }}
                            />
                            {values.nfIssuerCompanyName && (
                              <Row style={{ marginTop: 8 }}>
                                <Col span={24}>
                                  <Tag color="purple">
                                    NF: {values.nfIssuerCompanyName}
                                  </Tag>
                                </Col>
                              </Row>
                            )}
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item label={tf.cpfCnpj || "CPF/CNPJ"}>
                            <Input
                              disabled
                              placeholder={defaultPlaceholder}
                              value={
                                values.issuerCnpj
                                  ? cnpjMask(values.issuerCnpj)
                                  : ""
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      {/* <Alert
                        type="warning"
                        showIcon
                        message={
                          tf.pendingHint ||
                          'Se a NF ainda não foi registrada no módulo Financeiro, o movimento ficará com status "Pendente".'
                        }
                      /> */}
                    </FiscalBlock>
                  )}

                  {(values.origin === "PURCHASE" ||
                    values.origin === "SALE") && (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label={"Associar Financeiro ?"}>
                          <Switch
                            checked={values.showFinancialTransactionBox}
                            onChange={(checked) =>
                              setFieldValue(
                                "showFinancialTransactionBox",
                                checked,
                              )
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  {showFinancialTransactionBox && (
                    <FinancialTransactionBox
                      type={type}
                      origin={values.origin}
                      values={values.financialTransaction}
                      errors={errors.financialTransaction || {}}
                      submitCount={submitCount}
                      setFieldValue={(field, val) =>
                        setFieldValue(`financialTransaction.${field}`, val)
                      }
                      supplierTextSearch={textSearchSupplier}
                      totalItemsCost={totalItemsCost}
                      isEnablePaymentMethod={isEnablePaymentMethod}
                      isEnableFinancialIssue={isEnableFinancialIssue}
                      isEnableFinancialTaxableDeductible={
                        isEnableFinancialTaxableDeductible
                      }
                      isEnableBankAccountFarm={isEnableBankAccountFarm}
                    />
                  )}

                  {showWithDrawalFields && (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          required
                          label={
                            <span
                              className={
                                hasError("requester") ? "label-error" : ""
                              }
                            >
                              {tf.requester || "Solicitante"} *
                            </span>
                          }
                          validateStatus={hasError("requester") ? "error" : ""}
                          help={hasError("requester") ? errors.requester : null}
                        >
                          <Input
                            value={values.requester}
                            placeholder={defaultPlaceholder}
                            onChange={(e) =>
                              setFieldValue("requester", e.target.value)
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          required
                          label={
                            <span
                              className={
                                hasError("issueDate") ? "label-error" : ""
                              }
                            >
                              {tf.issueDate || "Data Retirada"} *
                            </span>
                          }
                          validateStatus={hasError("issueDate") ? "error" : ""}
                          help={hasError("issueDate") ? errors.issueDate : null}
                        >
                          <DatePicker
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            value={
                              values.issueDate ? moment(values.issueDate) : null
                            }
                            onChange={(date) =>
                              setFieldValue(
                                "issueDate",
                                date ? date.format("YYYY-MM-DD") : "",
                              )
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  {showProductionFields && (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          required
                          label={
                            <span
                              className={
                                hasError("productionDate") ? "label-error" : ""
                              }
                            >
                              {tf.productionDate || "Data de Produção"} *
                            </span>
                          }
                          validateStatus={
                            hasError("productionDate") ? "error" : ""
                          }
                          help={
                            hasError("productionDate")
                              ? errors.productionDate
                              : null
                          }
                        >
                          <DatePicker
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            value={
                              values.productionDate
                                ? moment(values.productionDate)
                                : null
                            }
                            onChange={(date) =>
                              setFieldValue(
                                "productionDate",
                                date ? date.format("YYYY-MM-DD") : "",
                              )
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  {values.origin && (
                    <>
                      <Divider orientation="left">
                        {tf.materials || "Materiais"}
                      </Divider>
                      {lines.map((line, index) => (
                        <StockMovementMaterialLine
                          key={index}
                          line={line}
                          index={index}
                          movementType={type}
                          receiptDate={values.receiptDate}
                          showErrors={submitCount > 0}
                          onChange={handleLineChange}
                          onRemove={handleRemoveLine}
                          canRemove={lines.length > 1}
                        />
                      ))}
                      <Button
                        type="dashed"
                        icon="plus"
                        onClick={handleAddLine}
                        style={{ marginBottom: 16 }}
                      >
                        {tf.addItem || "Adicionar Item"}
                      </Button>
                      {type === "ENTRY" && (
                        <TotalCostBox>
                          {tf.totalCost || "Custo Total dos Itens"}:
                          <strong>
                            {numberMask(totalItemsCost, true, null, null, 2, 2)}
                          </strong>
                        </TotalCostBox>
                      )}
                    </>
                  )}

                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item label={tf.observations || "Observações"}>
                        <TextArea
                          rows={2}
                          value={values.observations}
                          placeholder={defaultPlaceholder}
                          onChange={(e) =>
                            setFieldValue("observations", e.target.value)
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
                <Row
                  type="flex"
                  justify="end"
                  align="middle"
                  gutter={16}
                  style={{ marginTop: 16 }}
                >
                  <Col>
                    <ButtonStandard
                      type="button"
                      buttonType="type7"
                      size="s"
                      onClick={handleCancel}
                    >
                      {translation.buttons.cancel}
                    </ButtonStandard>
                  </Col>
                  <Col>
                    <ButtonStandard
                      type="button"
                      buttonType="type6"
                      onClick={handleSubmit}
                      disabled={isLoadingEdit}
                    >
                      {translation.buttons.save}
                    </ButtonStandard>
                  </Col>
                </Row>
              </StyledFormContainer>
            </Spin>

            <DanfeViewerModal
              visible={danfeVisible}
              nfReference={values.nfReference}
              groupId={groupId}
              farmId={farmId}
              onClose={() => setDanfeVisible(false)}
            />
          </Modal>
        );
      }}
    </Formik>
  );
};

export default StockMovementFormModal;
